// DiagnoseResult — 19BO+ Line 2 B4 · /ingest/diagnose endpoint 返回值契约
//
// 为什么存在 (llm-native.md §12.2 铁律 4):
//   diagnose 输出是只读 verdict，不包 EditableItem（与 evaluate 同）。本 schema
//   把 endpoint 返回值分两层：verdict（弱点诊断 + 补练建议 · LLM 产弱点/总结/建议 +
//   handler 注入 diagnosedAt/modelVersion）+ telemetry（计费/可观测性 · handler 注入
//   成本）。
//
// 与 EvaluateResultSchema 的结构差异（为何不复用）:
//   evaluate verdict 是「单题评分」（score + rubric + feedback），diagnose verdict
//   是「课程级弱项诊断」（weakness[] + overallSummary + suggestedPractice）。
//   两者 verdict 形状根本不同——evaluate 是一个分数 + 维度分解，diagnose 是多个
//   弱点条目 + 跨弱点建议。强行复用会让 schema 变成语义模糊的并集。各用独立
//   verdict schema 更清晰（与 evaluate/repair 各有独立 result schema 同构）。
//
// weakness 结构设计（与 WrongAnswerStats 数据源对齐）:
//   每个 weakness 归因到一个维度（chapter / category / cognitiveLevel 三选一 ·
//   对应 byCase/byCategory/byCognitive 三个聚合轴）+ severity（high/medium/low ·
//   LLM 按未解决数+频次判定）+ evidence（引用聚合数据的具体数字 · 可追溯）+
//   recommendation（针对该弱点的补练建议）。
//
// 为什么 verdict 不含 telemetry（分层）:
//   与 EvaluateResult 同理——verdict 是用户可见产品内容（弱点+建议），telemetry
//   是平台内部成本数据（对用户不可见 · 前端可丢弃）。
//
// 扩展契约（未来若 weakness 需新增归因维度）:
//   1. 加 WeaknessAttributionKind + WeaknessSchema.attribution 联合 variant
//   2. prompt.ts 的归因指令同步加新维度
//   零 endpoint 改动（handler 按 schema 校验）。
import { z } from 'zod';
// ─── weakness 归因维度（对齐 WrongAnswerStats 的三个聚合轴） ───────────────
/**
 * weakness 归因的三类维度。对齐 web/src/lib/stats.ts WrongAnswerStats 的三个
 * 聚合轴：byCase（chapter 分布）/ byCategory（题型分布）/ byCognitive（Bloom 分布）。
 * 顺序固定，反映从「在哪薄弱」到「哪类题薄弱」到「认知层级薄弱」的诊断深度。
 */
export const WEAKNESS_ATTRIBUTION_VALUES = [
    'chapter',
    'category',
    'cognitive',
];
export const WeaknessAttributionSchema = z.enum(WEAKNESS_ATTRIBUTION_VALUES);
/**
 * weakness 严重度。LLM 按未解决错题数 + 错误频次 + 趋势判定：
 *   high = 反复出错且未解决（必修）
 *   medium = 有错但部分解决或频次中（建议补）
 *   low = 偶发错误或已接近解决（留意）
 */
export const WEAKNESS_SEVERITY_VALUES = ['high', 'medium', 'low'];
export const WeaknessSeveritySchema = z.enum(WEAKNESS_SEVERITY_VALUES);
// ─── verdict（LLM 产出的诊断内容 · §12.2 铁律 4 只读 verdict） ──────────────
/**
 * 单条弱点诊断。LLM 基于 wrongAnswerAggregate/itemMetadata/timeSeries 三个 context
 * block 推断。每条弱点归因到一个维度（chapter/category/cognitive），附证据 + 建议。
 */
export const DiagnoseWeaknessSchema = z.object({
    /** 归因维度（chapter/category/cognitive 三选一 · 对齐 WrongAnswerStats 三轴）。 */
    attribution: WeaknessAttributionSchema,
    /** 归因目标的标签（如 chapter title / category 名 / cognitive level 名）。 */
    target: z.string().min(1)
        .describe('The label of the weak target (e.g. chapter title, category name, cognitive level).'),
    /** 严重度（LLM 按未解决数 + 频次 + 趋势判定）。 */
    severity: WeaknessSeveritySchema,
    /** 证据——引用聚合数据的具体数字，让诊断可追溯（如 "12 unresolved of 18 errors"）。 */
    evidence: z.string().min(1)
        .describe('Concrete numbers from the aggregate that justify this weakness (e.g. "12 unresolved of 18 errors").'),
    /** 针对该弱点的补练建议（actionable · 告诉学生补什么）。 */
    recommendation: z.string().min(1)
        .describe('Actionable practice suggestion for this specific weakness.'),
}).strict();
/**
 * diagnose verdict——LLM 产出的完整诊断。
 *
 * 三部分（LLM 产）：
 *   - weaknesses[]: 按严重度排序的弱点条目（high → medium → low）
 *   - overallSummary: 跨弱点的整体诊断（1-3 句话 · 模式识别）
 *   - suggestedPractice: 跨弱点的补练建议（下一步做什么 · 可包含生成建议）
 *
 * 元信息（handler 注入 · 与 evaluate verdict 同模式 · LLM 无法可靠报自己的时间戳/版本）：
 *   - diagnosedAt: 诊断发生的 ISO 时间
 *   - modelVersion: provider.name（forensic 追溯用 · 哪个模型诊断的）
 */
export const DiagnoseVerdictSchema = z.object({
    weaknesses: z.array(DiagnoseWeaknessSchema).min(1).max(12)
        .describe('Ranked weakness list (high severity first). 1-12 entries.'),
    overallSummary: z.string().min(1).max(800)
        .describe('Cross-weakness pattern synthesis (1-3 sentences). Markdown allowed.'),
    suggestedPractice: z.string().min(1).max(800)
        .describe('Next-step practice suggestion across all weaknesses. Markdown allowed.'),
    /** 诊断发生时间（handler 注入 · LLM 不可靠报自己的时间戳）。 */
    diagnosedAt: z.string().datetime()
        .describe('ISO datetime when diagnosed (server-injected).'),
    /** provider/model 身份（handler 注入 · forensic 追溯用）。 */
    modelVersion: z.string()
        .describe('Provider/model identity for forensic value (e.g. "deepseek:deepseek-v4-pro"). Server-injected.'),
}).strict();
// ─── telemetry（计费 + 可观测性 · 对用户不可见 · 对齐 EvaluateTelemetry） ────
/**
 * 单次 diagnose 调用的成本/延迟快照。形状对齐 EvaluateTelemetry（inputTokens/
 * outputTokens/costRmb/durationMs）—— diagnose 与 evaluate 同为 per-call 单次 LLM
 * 调用，telemetry 契约同源。
 */
const DiagnoseTelemetrySchema = z.object({
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    costRmb: z.number().nonnegative(),
    durationMs: z.number().int().nonnegative(),
}).strict();
// ─── DiagnoseResult（endpoint 返回值 · 分层 verdict + telemetry） ───────────
/**
 * /ingest/diagnose endpoint 返回值（§12.2 铁律 4 的可执行落地）。
 *
 * 两层：
 *   - verdict: 弱点诊断 + 补练建议（LLM 产弱点/总结/建议 · handler 注入 diagnosedAt/modelVersion）
 *   - telemetry: 成本/延迟（计费用 · 对用户不可见 · 前端可丢弃）
 *
 * 当前消费者：B4.3 前端 diagnose-card（待实装）。
 */
export const DiagnoseResultSchema = z.object({
    verdict: DiagnoseVerdictSchema,
    telemetry: DiagnoseTelemetrySchema,
}).strict();
//# sourceMappingURL=diagnose-result.js.map